import React, { useMemo } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import { All_Pages } from "utils/constants";
import { buildWebsiteJsonLd } from "utils/metaHelpers";

const Meta = React.memo(() => {
  const router = useRouter();

  const currentPath = (router?.isReady && router?.asPath) ? router.asPath : (router?.asPath || "/");
  // Strip query parameters for metadata lookup (e.g. /search?term=react -> /search)
  const cleanPath = useMemo(() => {
    return currentPath.split("?")[0];
  }, [currentPath]);

  const pageMetadata = useMemo(() => {
    try {
      const metadata = All_Pages[cleanPath] || All_Pages["/"];
      // #region agent log
      if (process.env.NODE_ENV === 'development') {
        console.log('[DEBUG Meta] Page metadata resolved', { currentPath, cleanPath, hasMetadata: !!metadata, title: metadata?.title });
      }
      // #endregion
      return metadata;
    } catch (error) {
      return All_Pages["/"];
    }
  }, [cleanPath]);

  const canonicalUrl = useMemo(() => {
    const baseUrl = "https://www.teammateme.com";
    const path = (router?.route && router.route !== "/") ? router.route : "";
    return `${baseUrl}${path}`;
  }, [router?.route]);

  const ogImageUrl = useMemo(() => {
    if (!pageMetadata || !pageMetadata["og:image"]) {
      return "https://www.teammateme.com/teammateme_banner_img.jpg";
    }
    return `https://www.teammateme.com${pageMetadata["og:image"]}`;
  }, [pageMetadata]);

  const websiteJsonLd = useMemo(
    () => buildWebsiteJsonLd(pageMetadata),
    [pageMetadata]
  );

  return (
    <Head>
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1.0"
      />
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/apple-touch-icon.png"
      />
      <link
        rel="icon"
        sizes="16x16"
        type="image/svg+xml"
        href="/favicon16.svg"
      />
      <link
        rel="icon"
        sizes="32x32"
        type="image/svg+xml"
        href="/favicon32.svg"
      />
      <link rel="manifest" href="/site.webmanifest" />
      <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#324d66" />
      <meta name="msapplication-TileColor" content="#324d66" />
      <meta name="theme-color" content="#324d66" />
      <link rel="canonical" href={canonicalUrl} />
      <title>{pageMetadata?.title || "TeammateMe"}</title>
      <meta name="description" content={pageMetadata?.description || ""} />
      <meta property="og:url" content={pageMetadata?.["og:url"] || "https://www.teammateme.com"} />
      <meta property="og:type" content={pageMetadata?.["og:type"] || "website"} />
      <meta property="og:title" content={pageMetadata?.title || "TeammateMe"} />
      <meta property="og:description" content={pageMetadata?.description || ""} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/jpeg" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:domain" content="teammateme.com" />
      <meta name="twitter:url" content={pageMetadata?.["og:url"] || "https://www.teammateme.com"} />
      <meta name="twitter:title" content={pageMetadata?.title || "TeammateMe"} />
      <meta name="twitter:description" content={pageMetadata?.description || ""} />
      <meta name="twitter:image" content={ogImageUrl} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(websiteJsonLd),
        }}
      />
    </Head>
  );
});

Meta.displayName = "Meta";

export default Meta;
